import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Building, User, UserPlus, Users, X } from 'lucide-react';

const MailUnknownContactModal = ({
  companiesList,
  createQuickAnnuaireContact,
  createQuickClient,
  createQuickCourtier,
  creatingContact,
  quickAnnuaireForm,
  quickClientForm,
  quickCourtierForm,
  setQuickAnnuaireForm,
  setQuickClientForm,
  setQuickCourtierForm,
  setShowUnknownContactModal,
  setUnknownContactStep,
  setUnknownContactType,
  unknownContactEmail,
  unknownContactStep,
  unknownContactType
}) => {
  const { t } = useTranslation('admin');
  return (
    <>
        <div data-testid="mail-unknown-contact-modal" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-900 flex items-center">
                  <UserPlus size={24} className="mr-3 text-red-600" />
                  {t('mail.uc.createContact')}
                </h3>
                <button
                  onClick={() => setShowUnknownContactModal(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-sm text-slate-500 mt-2">
                {t('mail.uc.emailColon')} <span className="font-medium text-red-600">{unknownContactEmail}</span>
              </p>
            </div>

            <div className="p-6">
              {/* Étape 1 : Choix du type */}
              {unknownContactStep === 1 && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600 mb-4">
                    {t('mail.uc.chooseWhereCreate')}
                  </p>
                  
                  <button
                    onClick={() => { setUnknownContactType('client'); setUnknownContactStep(2); }}
                    className="w-full p-4 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 hover:border-blue-400 rounded-xl text-left transition-all"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <User size={24} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{t('mail.linkedClient')}</p>
                        <p className="text-sm text-slate-500">{t('mail.uc.clientCreateDesc')}</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => { setUnknownContactType('courtier'); setUnknownContactStep(2); }}
                    className="w-full p-4 bg-green-50 hover:bg-green-100 border-2 border-green-200 hover:border-green-400 rounded-xl text-left transition-all"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <Users size={24} className="text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{t('mail.linkedCourtier')}</p>
                        <p className="text-sm text-slate-500">{t('mail.uc.courtierCreateDesc')}</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => { setUnknownContactType('annuaire'); setUnknownContactStep(2); }}
                    className="w-full p-4 bg-purple-50 hover:bg-purple-100 border-2 border-purple-200 hover:border-purple-400 rounded-xl text-left transition-all"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <Building size={24} className="text-purple-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{t('mail.uc.annuaireTitle')}</p>
                        <p className="text-sm text-slate-500">{t('mail.uc.annuaireDesc')}</p>
                      </div>
                    </div>
                  </button>
                </div>
              )}

              {/* Étape 2 : Formulaire Client */}
              {unknownContactStep === 2 && unknownContactType === 'client' && (
                <div className="space-y-4">
                  <button
                    onClick={() => setUnknownContactStep(1)}
                    className="text-sm text-slate-500 hover:text-slate-700 flex items-center mb-4"
                  >
                    <ArrowLeft size={16} className="mr-1" /> {t('mail.back')}
                  </button>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.sourceRequired')}</label>
                    <select
                      value={quickClientForm.source}
                      onChange={(e) => setQuickClientForm({...quickClientForm, source: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Client direct ASAR">{t('mail.uc.optSourceDirectAsar')}</option>
                      <option value="Partenaire">{t('mail.uc.optSourcePartner')}</option>
                      <option value="Internet">{t('mail.uc.optSourceInternet')}</option>
                      <option value="Recommandation">{t('mail.uc.optSourceReco')}</option>
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.firstNameRequired')}</label>
                      <input
                        type="text"
                        value={quickClientForm.prenom}
                        onChange={(e) => setQuickClientForm({...quickClientForm, prenom: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder={t('mail.uc.firstNamePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.lastNameRequired')}</label>
                      <input
                        type="text"
                        value={quickClientForm.nom}
                        onChange={(e) => setQuickClientForm({...quickClientForm, nom: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder={t('mail.uc.lastNamePlaceholder')}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.emailRequired')}</label>
                      <input
                        type="email"
                        value={quickClientForm.email}
                        onChange={(e) => setQuickClientForm({...quickClientForm, email: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-slate-50"
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.phoneRequired')}</label>
                      <input
                        type="tel"
                        value={quickClientForm.telephone}
                        onChange={(e) => setQuickClientForm({...quickClientForm, telephone: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder={t('mail.uc.phonePlaceholder')}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.typeLabel')}</label>
                      <select
                        value={quickClientForm.type}
                        onChange={(e) => setQuickClientForm({...quickClientForm, type: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Particulier">{t('mail.uc.optParticulier')}</option>
                        <option value="Professionnel">{t('mail.uc.optProfessionnel')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.statusLabel')}</label>
                      <select
                        value={quickClientForm.statut}
                        onChange={(e) => setQuickClientForm({...quickClientForm, statut: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Non actif (pas de contrat)">{t('mail.uc.optInactif')}</option>
                        <option value="Actif">{t('mail.uc.optActif')}</option>
                        <option value="Prospect">{t('mail.uc.optProspect')}</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.companyLabel')}</label>
                    <input
                      type="text"
                      value={quickClientForm.entreprise}
                      onChange={(e) => setQuickClientForm({...quickClientForm, entreprise: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder={t('mail.uc.companyOptionalPlaceholder')}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.notesLabel')}</label>
                    <textarea
                      value={quickClientForm.notes}
                      onChange={(e) => setQuickClientForm({...quickClientForm, notes: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows="2"
                      placeholder={t('mail.uc.notesOptionalPlaceholder')}
                    />
                  </div>
                  
                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={() => setShowUnknownContactModal(false)}
                      className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                    >
                      {t('mail.cancelBtn')}
                    </button>
                    <button
                      onClick={createQuickClient}
                      disabled={!quickClientForm.prenom || !quickClientForm.nom || creatingContact}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
                    >
                      {creatingContact ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        t('mail.uc.createClientBtn')
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Étape 2 : Formulaire Courtier */}
              {unknownContactStep === 2 && unknownContactType === 'courtier' && (
                <div className="space-y-4">
                  <button
                    onClick={() => setUnknownContactStep(1)}
                    className="text-sm text-slate-500 hover:text-slate-700 flex items-center mb-4"
                  >
                    <ArrowLeft size={16} className="mr-1" /> {t('mail.back')}
                  </button>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.firstNameRequired')}</label>
                      <input
                        type="text"
                        value={quickCourtierForm.prenom}
                        onChange={(e) => setQuickCourtierForm({...quickCourtierForm, prenom: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        placeholder={t('mail.uc.courtierFirstNamePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.lastNameRequired')}</label>
                      <input
                        type="text"
                        value={quickCourtierForm.nom}
                        onChange={(e) => setQuickCourtierForm({...quickCourtierForm, nom: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        placeholder={t('mail.uc.courtierLastNamePlaceholder')}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.emailRequired')}</label>
                    <input
                      type="email"
                      value={quickCourtierForm.email}
                      onChange={(e) => setQuickCourtierForm({...quickCourtierForm, email: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 bg-slate-50"
                      readOnly
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.phoneRequired')}</label>
                    <input
                      type="tel"
                      value={quickCourtierForm.telephone}
                      onChange={(e) => setQuickCourtierForm({...quickCourtierForm, telephone: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder={t('mail.uc.phonePlaceholder')}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.oriasLabel')}</label>
                    <input
                      type="text"
                      value={quickCourtierForm.orias}
                      onChange={(e) => setQuickCourtierForm({...quickCourtierForm, orias: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder={t('mail.uc.oriasPlaceholder')}
                      maxLength={8}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.typeLabel')}</label>
                    <select
                      value={quickCourtierForm.type}
                      onChange={(e) => setQuickCourtierForm({...quickCourtierForm, type: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="independant">{t('mail.uc.optIndependant')}</option>
                      <option value="reseau">{t('mail.uc.optReseau')}</option>
                      <option value="agent">{t('mail.uc.optAgent')}</option>
                      <option value="compagnie">{t('mail.uc.optCompagnie')}</option>
                      <option value="brokoly">{t('mail.uc.optBrokoly')}</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.specialtyLabel')}</label>
                    <input
                      type="text"
                      value={quickCourtierForm.specialite}
                      onChange={(e) => setQuickCourtierForm({...quickCourtierForm, specialite: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder={t('mail.uc.specialtyPlaceholder')}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.commentsLabel')}</label>
                    <textarea
                      value={quickCourtierForm.commentaires}
                      onChange={(e) => setQuickCourtierForm({...quickCourtierForm, commentaires: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      rows="2"
                      placeholder={t('mail.uc.commentsPlaceholder')}
                    />
                  </div>
                  
                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={() => setShowUnknownContactModal(false)}
                      className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                    >
                      {t('mail.cancelBtn')}
                    </button>
                    <button
                      onClick={createQuickCourtier}
                      disabled={!quickCourtierForm.prenom || !quickCourtierForm.nom || creatingContact}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
                    >
                      {creatingContact ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        t('mail.uc.createCourtierBtn')
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Étape 2 : Formulaire Annuaire */}
              {unknownContactStep === 2 && unknownContactType === 'annuaire' && (
                <div className="space-y-4">
                  <button
                    onClick={() => setUnknownContactStep(1)}
                    className="text-sm text-slate-500 hover:text-slate-700 flex items-center mb-4"
                  >
                    <ArrowLeft size={16} className="mr-1" /> {t('mail.back')}
                  </button>
                  
                  {/* Choix compagnie existante ou nouvelle */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-slate-700">{t('mail.uc.companyLabelDir')}</label>
                    
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={!quickAnnuaireForm.isNewCompany}
                          onChange={() => setQuickAnnuaireForm({...quickAnnuaireForm, isNewCompany: false})}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('mail.uc.existing')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={quickAnnuaireForm.isNewCompany}
                          onChange={() => setQuickAnnuaireForm({...quickAnnuaireForm, isNewCompany: true})}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('mail.uc.newFem')}</span>
                      </label>
                    </div>
                    
                    {!quickAnnuaireForm.isNewCompany ? (
                      <select
                        value={quickAnnuaireForm.companyId}
                        onChange={(e) => setQuickAnnuaireForm({...quickAnnuaireForm, companyId: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">{t('mail.uc.selectCompany')}</option>
                        {companiesList.map(company => (
                          <option key={company.id} value={company.id}>{company.name}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={quickAnnuaireForm.newCompanyName}
                        onChange={(e) => setQuickAnnuaireForm({...quickAnnuaireForm, newCompanyName: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder={t('mail.uc.newCompanyPlaceholder')}
                      />
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.contactNameRequired')}</label>
                    <input
                      type="text"
                      value={quickAnnuaireForm.contactName}
                      onChange={(e) => setQuickAnnuaireForm({...quickAnnuaireForm, contactName: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder={t('mail.uc.contactNamePlaceholder')}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.serviceLabel')}</label>
                    <input
                      type="text"
                      value={quickAnnuaireForm.service}
                      onChange={(e) => setQuickAnnuaireForm({...quickAnnuaireForm, service: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder={t('mail.uc.servicePlaceholder')}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.emailRequired')}</label>
                    <input
                      type="email"
                      value={quickAnnuaireForm.email}
                      onChange={(e) => setQuickAnnuaireForm({...quickAnnuaireForm, email: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-slate-50"
                      readOnly
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('mail.uc.phoneLabel')}</label>
                    <input
                      type="tel"
                      value={quickAnnuaireForm.telephone}
                      onChange={(e) => setQuickAnnuaireForm({...quickAnnuaireForm, telephone: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder={t('mail.uc.phoneOptionalPlaceholder')}
                    />
                  </div>
                  
                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={() => setShowUnknownContactModal(false)}
                      className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                    >
                      {t('mail.cancelBtn')}
                    </button>
                    <button
                      onClick={createQuickAnnuaireContact}
                      disabled={
                        !quickAnnuaireForm.contactName || 
                        (!quickAnnuaireForm.isNewCompany && !quickAnnuaireForm.companyId) ||
                        (quickAnnuaireForm.isNewCompany && !quickAnnuaireForm.newCompanyName) ||
                        creatingContact
                      }
                      className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center"
                    >
                      {creatingContact ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        t('mail.uc.addToDirectoryBtn')
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
    </>
  );
};

export default MailUnknownContactModal;
